import { SocialActionResponse, SocialCommentDetailResponse, SocialCommentDismissResponse, SocialCommentListResponse, SocialConnectionDeleteResponse, SocialConnectionResponse, SocialConnectionResumeResponse, SocialConnectionStopResponse, SocialConnectionsResponse, SocialGlobalStopResponse, SocialPoliciesResponse, SocialPollResponse, SocialSettingsResponse, SocialSettingsUpdateResponse, SocialShadowSummaryResponse, } from '@wit-support/admin-contracts';
import { createModuleApi, unwrapCoded } from '@wit-support/admin-client-core';
/**
 * Запросы раздела «Соцсети» к шлюзу admin-api `/api/social/*`.
 *
 * Ошибки — общий `ModuleRequestError` из `admin-client-core` (своего класса
 * у модуля нет): `code` — код admin-api (`upstream_rejected`,
 * `social_engine_not_configured`, `forbidden`…), `upstreamCode` — код движка
 * (`social_not_enabled`, `social_token_mismatch`…), `serverMessage` — русская
 * фраза admin-api. Экран решает по кодам, а не по тексту.
 */
const api = createModuleApi();
export function setSocialApiBaseUrl(url) {
    api.setBaseUrl(url);
}
const BASE = '/api/social';
function jsonInit(method, body) {
    return { method, headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) };
}
/**
 * Запрос + разбор ответа СХЕМОЙ контракта: значения по умолчанию
 * (`.nullable().default(null)`) проставляются здесь, а незнакомое значение
 * поля, по которому человек действует (статус, режим), роняет разбор, а не
 * показывает «активно».
 */
async function request(schema, path, init) {
    const r = await api.fetchRaw(path, init);
    return schema.parse(await unwrapCoded(r));
}
function qs(params) {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
        if (v !== undefined && v !== '')
            p.set(k, String(v));
    }
    const s = p.toString();
    return s ? `?${s}` : '';
}
const seg = (v) => encodeURIComponent(v);
const connectionPath = (k) => `${BASE}/connections/${seg(k.account)}/${seg(k.network)}/${seg(k.authType)}`;
/** GET /connections — таблица «аккаунт × сеть» + флаги модуля. */
export function listConnections() {
    return request(SocialConnectionsResponse, `${BASE}/connections`);
}
/** PUT /connections/:account/:network/:authType — подключить или заменить токен. */
export function putConnection(key, token) {
    return request(SocialConnectionResponse, connectionPath(key), jsonInit('PUT', { token }));
}
/** POST …/check — проверить токен в сети прямо сейчас. */
export function checkConnection(key) {
    return request(SocialConnectionResponse, `${connectionPath(key)}/check`, { method: 'POST' });
}
/**
 * POST …/reuse — «Подключить тем же токеном, что у Instagram» (FB-SPEC §0.4):
 * движок сам копирует зашифрованный токен подключения
 * `(account, fromNetwork, authType)` и проверяет его, как при вставке. Токена
 * нет ни в запросе, ни в ответе. Только владелец (`social.connections_manage`).
 */
export function reuseConnection(key, fromNetwork) {
    return request(SocialConnectionResponse, `${connectionPath(key)}/reuse`, jsonInit('POST', { fromNetwork }));
}
/** DELETE … — отключить: токен удаляется в движке. */
export function deleteConnection(key) {
    return request(SocialConnectionDeleteResponse, connectionPath(key), { method: 'DELETE' });
}
/** GET /settings — настройки реагирования по аккаунтам и сетям. */
export function listSettings() {
    return request(SocialSettingsResponse, `${BASE}/settings`);
}
/** PUT /settings/:account/:network — только изменённые поля. */
export function updateSettings(key, body) {
    return request(SocialSettingsUpdateResponse, `${BASE}/settings/${seg(key.account)}/${seg(key.network)}`, jsonInit('PUT', body));
}
/** GET /policies — алгоритмы выбора действия. */
export function listPolicies() {
    return request(SocialPoliciesResponse, `${BASE}/policies`);
}
/** GET /comments — журнал комментариев, новые сверху. */
export function listComments(p) {
    return request(SocialCommentListResponse, `${BASE}/comments${qs({
        account: p.account,
        network: p.network,
        state: p.state,
        pending: p.pending === undefined ? undefined : String(p.pending),
        parentId: p.parentId,
        from: p.from,
        q: p.q,
        category: p.category,
        needsHuman: p.needsHuman === undefined ? undefined : String(p.needsHuman),
        shadow: p.shadow === undefined ? undefined : String(p.shadow),
        page: p.page,
        pageSize: p.pageSize,
    })}`);
}
/** GET /comments/:id — карточка: комментарий, журнал действий, пост, «что умеем». */
export function getComment(id) {
    return request(SocialCommentDetailResponse, `${BASE}/comments/${seg(id)}`);
}
/**
 * POST /comments/:id/dismiss — «Отметить просмотренным»: комментарий уходит
 * из «Ждут решения» без действий. В сеть ничего не уходит; повтор безопасен.
 */
export function dismissComment(id) {
    return request(SocialCommentDismissResponse, `${BASE}/comments/${seg(id)}/dismiss`, {
        method: 'POST',
    });
}
/**
 * POST /comments/:id/actions — ручное действие. Это же «Повторить» после
 * `failed`/`skipped`: новая запись, старая остаётся в журнале.
 */
export function requestAction(commentId, body) {
    return request(SocialActionResponse, `${BASE}/comments/${seg(commentId)}/actions`, jsonInit('POST', body));
}
/**
 * POST /actions/:id/approve — подтвердить предложенное алгоритмом. `text` —
 * исправленный текст ответа (уходит в теле); без него — как есть, без тела.
 */
export function approveAction(id, body = {}) {
    const path = `${BASE}/actions/${seg(id)}/approve`;
    return body.text !== undefined
        ? request(SocialActionResponse, path, jsonInit('POST', body))
        : request(SocialActionResponse, path, { method: 'POST' });
}
/** POST /actions/:id/reject — отклонить предложенное. */
export function rejectAction(id, reason) {
    return request(SocialActionResponse, `${BASE}/actions/${seg(id)}/reject`, jsonInit('POST', reason !== undefined ? { reason } : {}));
}
/**
 * GET /shadow/summary — «Тень за период»: что алгоритм сделал бы в режиме
 * «Тень» по аккаунту × сети (RULINGS-SHADOW). Только чтение.
 */
export function getShadowSummary(p) {
    return request(SocialShadowSummaryResponse, `${BASE}/shadow/summary${qs({ account: p.account, network: p.network, from: p.from, to: p.to })}`);
}
/** POST /poll — «Опросить сейчас» (в фоне). */
export function runPoll(body = {}) {
    return request(SocialPollResponse, `${BASE}/poll`, jsonInit('POST', body));
}
/**
 * POST /stop — «Стоп всех действий»: движок перестаёт исполнять действия всех
 * аккаунтов. Супервайзер и владелец (выключающее действие, FX-SS2).
 */
export function stopAll() {
    return request(SocialGlobalStopResponse, `${BASE}/stop`, { method: 'POST' });
}
/** POST /resume — «Возобновить все действия» (снять общий стоп). Только владелец. */
export function resumeAll() {
    return request(SocialGlobalStopResponse, `${BASE}/resume`, { method: 'POST' });
}
/**
 * POST /connections/:account/:network/stop — «Остановить аккаунт»: действия
 * этого аккаунта × сети не уходят в сеть до «Возобновить». Супервайзер и
 * владелец (выключающее действие).
 */
export function stopConnection(key) {
    return request(SocialConnectionStopResponse, `${BASE}/connections/${seg(key.account)}/${seg(key.network)}/stop`, { method: 'POST' });
}
/**
 * POST /connections/:account/:network/resume — «Возобновить» аккаунт после
 * блокировки сети или ручного стопа. Только владелец. Ответ — какие паузы
 * движок снял и какие остались (истекут сами) и в какой режим вернул аккаунт.
 * `confirmBlock` (RULINGS-SAFETY-3 M2) — человек подтвердил, что ограничение
 * Instagram снято: без него движок не снимает действующую паузу 368 (409
 * `social_block_confirm_required`). Без подтверждения — без тела, как раньше.
 */
export function resumeConnection(key, opts = {}) {
    const url = `${BASE}/connections/${seg(key.account)}/${seg(key.network)}/resume`;
    return request(SocialConnectionResumeResponse, url, opts.confirmBlock === true ? jsonInit('POST', { confirmBlock: true }) : { method: 'POST' });
}
//# sourceMappingURL=api.js.map